import instance from "../../index";
import {useEffect, useState} from "react";
import {useBlockUI} from "../../../shared/services/block-ui-service";
import {Cliente} from "../../../models/cliente";
import {PagedList} from "../../../models/paged-list";
import {BaseResponse} from "../../../models/base-response";
import {ShortDto} from "../../../models/short-dto";
import {ClientFilter} from "../../../filters/client-filter";
import {CreateClientRequest} from "../../../models/requests/create-client-request";
import {EditClientRequest} from "../../../models/requests/edit-client-request";
import {CreateClientDriverRequest} from "../../../models/requests/create-client-driver-request";
import {ClientDriver} from "../../../models/clientDriver";
import {Driver} from "../../../models/driver";
import {ClientDriverFilters} from "../../../filters/client-driver-filters";

const CLIENTS_SHORT_CACHE_KEY = 'fleetgame:clients:short';
const CLIENTS_SHORT_CACHE_TTL_MS = 15 * 60 * 1000;

const useClientService = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [clientId, setClientId] = useState<string>();
    const blockUI = useBlockUI();
    useEffect(() => {
        const clientId = localStorage.getItem('clientId');
        if (clientId) {
            setClientId(clientId);
        }
    }, []);

    useEffect(() => {
        blockUI?.setActive(loading)
    }, [loading]);

    async function activateClient(id: string): Promise<Cliente> {
        const response = await instance.put<Cliente>(`/v1/backoffice/clients/${id}/activate`);
        return response.data;
    }

    async function inactivateClient(id: string): Promise<Cliente> {
        const response = await instance.put<Cliente>(`/v1/backoffice/clients/${id}/inactivate`);
        return response.data;
    }

    async function deleteClient(id: string) {
        const response = await instance.delete(`/v1/backoffice/clients/${id}`);
        return response?.data;
    }

    async function getClientById(id: string): Promise<Cliente> {
        const response = await instance.get<BaseResponse<Cliente>>(`/v1/backoffice/clients/${id}`);
        return response?.data?.data;
    }

    async function getClients(filter?: ClientFilter): Promise<PagedList<Cliente>> {
        const response = await instance.get<PagedList<Cliente>>('/v1/backoffice/clients/', {
            params: filter
        });
        return response.data;
    }

    async function getAllClientsShortDto(filter?: ClientFilter): Promise<ShortDto[]> {
        try {
            const cachedRaw = sessionStorage.getItem(CLIENTS_SHORT_CACHE_KEY);
            if (cachedRaw) {
                const cached = JSON.parse(cachedRaw) as { data: ShortDto[]; expiresAt: number };
                if (cached.expiresAt > Date.now()) {
                    return cached.data;
                }
            }
        } catch {
            sessionStorage.removeItem(CLIENTS_SHORT_CACHE_KEY);
        }

        const response = await instance.get<BaseResponse<ShortDto[]>>('/v1/backoffice/clients/all', {
            params: filter
        });
        const data = response?.data?.data ?? [];

        sessionStorage.setItem(
            CLIENTS_SHORT_CACHE_KEY,
            JSON.stringify({ data, expiresAt: Date.now() + CLIENTS_SHORT_CACHE_TTL_MS }),
        );

        return data;
    }

    async function createClient(request: Partial<CreateClientRequest>): Promise<Cliente> {
        const response = await instance.post<Cliente>(`/v1/backoffice/clients/`, request);
        return response?.data;
    }

    async function editClient(request: Partial<EditClientRequest>, id: string | string[] | undefined): Promise<Cliente> {
        const response = await instance.put<Cliente>(`/v1/backoffice/clients/${id}`, request);
        return response?.data;
    }

    async function linkClientDriver(request: Partial<CreateClientDriverRequest>): Promise<ClientDriver> {
        const response = await instance.post<ClientDriver>(`/v1/backoffice/clients/drivers`, request);
        return response?.data;
    }

    async function deleteClientDriver(clientId: string, driverId: string) {
        const response = await instance.delete(`/v1/backoffice/clients/${clientId}/driver/${driverId}`);
        return response?.data;
    }

    async function getClientDrivers(filter?: ClientDriverFilters): Promise<PagedList<ClientDriver>> {
        const response = await instance.get<BaseResponse<PagedList<ClientDriver>>>(`/v1/backoffice/clients/drivers`,
            {
            params: filter
        });
        return response.data.data;
    }



    return {
        activateClient,
        deleteClient,
        getClientById,
        getClients,
        getAllClientsShortDto,
        inactivateClient,
        createClient,
        editClient,
        linkClientDriver: linkClientDriver,
        deleteClientDriver,
        getClientDrivers
    };
};

export default useClientService;
